import { format } from 'date-fns';
import { enUS, it } from 'date-fns/locale';
import { FileImage, FileSpreadsheet, FileText, LucideIcon } from 'lucide-react';
import type { TFunction } from 'i18next';
import {
  DOCUMENT_CATEGORIES,
  DocumentCategory,
  DocumentResponse,
  documentViewRule,
} from '@timesheet/schemas';

export const isDocumentCategory = (value: string | null | undefined): value is DocumentCategory =>
  !!value && (DOCUMENT_CATEGORIES as readonly string[]).includes(value);

/** OPERATOR and MANAGER land on their own payslips; admin-like roles on GENERALE. */
export const defaultTab = (role: string | undefined): DocumentCategory =>
  documentViewRule(role, 'BUSTE_PAGA') === 'own' ? 'BUSTE_PAGA' : 'GENERALE';

export const dateLocaleFor = (language: string | undefined) => (language?.startsWith('it') ? it : enUS);

/** "Settembre 2026" / "Tredicesima 2026" / "CU 2025"; the title for everything else. */
export const periodLabel = (doc: DocumentResponse, t: TFunction, language?: string): string => {
  if (doc.category !== 'BUSTE_PAGA' || !doc.payslipYear) return doc.title;
  const year = doc.payslipYear;
  switch (doc.payslipType) {
    case 'TREDICESIMA':
      return `${t('documents.payslipTypes.TREDICESIMA', 'Tredicesima')} ${year}`;
    case 'QUATTORDICESIMA':
      return `${t('documents.payslipTypes.QUATTORDICESIMA', 'Quattordicesima')} ${year}`;
    case 'CU':
      return `CU ${year}`;
    default: {
      if (!doc.payslipMonth) return doc.title;
      const monthName = format(new Date(year, doc.payslipMonth - 1, 1), 'LLLL', { locale: dateLocaleFor(language) });
      const label = `${monthName.charAt(0).toUpperCase()}${monthName.slice(1)} ${year}`;
      return doc.payslipType === 'ALTRO' ? `${t('documents.payslipTypes.ALTRO', 'Cedolino')} ${label}` : label;
    }
  }
};

export const formatSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const iconForMime = (mime: string): LucideIcon => {
  if (mime.startsWith('image/')) return FileImage;
  if (mime.includes('spreadsheet')) return FileSpreadsheet;
  return FileText;
};

export const ctaLabel = (category: DocumentCategory, t: TFunction): string => {
  switch (category) {
    case 'BUSTE_PAGA':
      return t('documents.cta.payslips', 'Carica buste paga');
    case 'CONTRATTI':
      return t('documents.cta.contract', 'Carica contratto');
    default:
      return t('documents.cta.document', 'Carica documento');
  }
};

export const categoryLabel = (category: DocumentCategory, t: TFunction): string => {
  const fallbacks: Record<DocumentCategory, string> = {
    GENERALE: 'Generale',
    BUSTE_PAGA: 'Buste paga',
    CONTRATTI: 'Contratti',
    AZIENDALI: 'Aziendali',
  };
  return t(`documents.categories.${category}`, fallbacks[category]);
};

export const monthNames = (language?: string): string[] =>
  Array.from({ length: 12 }, (_, i) => {
    const name = format(new Date(2026, i, 1), 'LLLL', { locale: dateLocaleFor(language) });
    return `${name.charAt(0).toUpperCase()}${name.slice(1)}`;
  });

/** Previous month, the most common period being uploaded. */
export const previousPeriod = (now = new Date()): { year: number; month: number } => {
  const month = now.getMonth(); // 0-based current month = previous month 1-based
  return month === 0 ? { year: now.getFullYear() - 1, month: 12 } : { year: now.getFullYear(), month };
};

export const formatDate = (iso: string, language?: string): string =>
  format(new Date(iso), 'd MMM yyyy', { locale: dateLocaleFor(language) });
